const VERSION = 1;
const STORAGE_PREFIX = 'foxy.feed.v1:';
const HISTORY_KEY = 'foxyFeed';
const RETURN_HISTORY_KEY = 'foxyFeedReturn';
const PENDING_RETURN_KEY = 'foxy.feed.return.pending.v1';
const VIEWER_STORAGE_KEY = 'foxy.feed.viewer.v1';
const RETURN_TTL_MS = 6 * 60 * 60 * 1000;
const NEW_POST_CHECK_INTERVAL_MS = 60 * 1000;
const MAX_DESCRIPTORS = 1000;
const MAX_SKIPPED_COUNT = 1000000;
const SESSION_ID_PATTERN = /^[a-zA-Z0-9_-]{16,128}$/;
const CARD_KEY_PATTERN = /^(discussion|review|quote|fact|collection):([1-9]\d*)$/;
const FILTERS = new Set(['all', 'discussion', 'review', 'quote', 'fact', 'collection']);
const PLACEMENTS = new Set(['pinned', 'organic']);

function now() {
    return Date.now();
}

function randomId(cryptoObject = typeof crypto === 'undefined' ? null : crypto) {
    if (cryptoObject && typeof cryptoObject.randomUUID === 'function') {
        return cryptoObject.randomUUID();
    }

    if (cryptoObject && typeof cryptoObject.getRandomValues === 'function') {
        const bytes = new Uint8Array(18);
        cryptoObject.getRandomValues(bytes);

        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    return `${now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function storageKey(sessionId) {
    return `${STORAGE_PREFIX}${sessionId}`;
}

function validSessionId(value) {
    return typeof value === 'string' && SESSION_ID_PATTERN.test(value);
}

function canonicalFilter(value) {
    return typeof value === 'string' && FILTERS.has(value) ? value : null;
}

function viewerMarker(documentObject = typeof document === 'undefined' ? null : document) {
    const marker = documentObject && typeof documentObject.querySelector === 'function'
        ? documentObject.querySelector('meta[name="user-id"]')
        : null;
    const userId = marker ? marker.getAttribute('content') : '';

    return userId && /^[1-9]\d*$/.test(userId) ? userId : 'guest';
}

function validViewerMarker(value) {
    return value === 'guest' || (typeof value === 'string' && /^[1-9]\d*$/.test(value));
}

function currentHistorySession(windowObject = typeof window === 'undefined' ? null : window) {
    const value = windowObject && windowObject.history && windowObject.history.state
        ? windowObject.history.state[HISTORY_KEY]
        : null;

    return value && value.v === VERSION && validSessionId(value.id) ? value.id : null;
}

function writeHistorySession(sessionId, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.history || !validSessionId(sessionId)) return false;

    const previous = windowObject.history.state && typeof windowObject.history.state === 'object'
        ? windowObject.history.state
        : {};
    try {
        windowObject.history.replaceState({
            ...previous,
            [HISTORY_KEY]: { v: VERSION, id: sessionId },
        }, '', windowObject.location.href);
    } catch (_) {
        return false;
    }

    return true;
}

function createHistorySession(windowObject = typeof window === 'undefined' ? null : window) {
    const id = randomId(windowObject ? windowObject.crypto : null);
    if (!writeHistorySession(id, windowObject)) return null;

    return id;
}

function safeRemove(sessionId, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage || !validSessionId(sessionId)) return;
    try {
        windowObject.sessionStorage.removeItem(storageKey(sessionId));
    } catch (_) {
        // Storage is optional. A failed cleanup must not break navigation.
    }
}

function normalizeDescriptors(value) {
    if (!Array.isArray(value) || value.length > MAX_DESCRIPTORS) return null;

    const seen = new Set();
    const descriptors = [];
    for (const descriptor of value) {
        if (!descriptor || typeof descriptor !== 'object'
            || typeof descriptor.key !== 'string'
            || !CARD_KEY_PATTERN.test(descriptor.key)
            || !PLACEMENTS.has(descriptor.placement)
            || seen.has(descriptor.key)) {
            return null;
        }
        seen.add(descriptor.key);
        descriptors.push({ key: descriptor.key, placement: descriptor.placement });
    }

    return descriptors;
}

function normalizeRecord(value) {
    if (!value || typeof value !== 'object' || value.v !== VERSION) return null;
    const filter = canonicalFilter(value.filter);
    const descriptors = normalizeDescriptors(value.descriptors);
    const marker = value.viewer_marker;
    if (!filter || descriptors === null
        || (marker !== 'guest' && !(typeof marker === 'string' && /^[1-9]\d*$/.test(marker)))
        || (value.high_water_token !== null && typeof value.high_water_token !== 'string')
        || (value.next_cursor !== null && typeof value.next_cursor !== 'string')
        || typeof value.has_more !== 'boolean'
        || !Number.isInteger(value.skipped_count) || value.skipped_count < 0
        || value.skipped_count > MAX_SKIPPED_COUNT
        || (value.anchor_key !== null && (typeof value.anchor_key !== 'string' || !CARD_KEY_PATTERN.test(value.anchor_key)))
        || (value.anchor_offset !== null && !Number.isFinite(value.anchor_offset))
        || !Number.isFinite(value.scroll_y) || value.scroll_y < 0
        || !Number.isFinite(value.last_new_post_check_at) || value.last_new_post_check_at < 0) {
        return null;
    }

    return {
        v: VERSION,
        filter,
        viewer_marker: marker,
        high_water_token: value.high_water_token,
        next_cursor: value.next_cursor,
        has_more: value.has_more,
        skipped_count: value.skipped_count,
        descriptors,
        anchor_key: value.anchor_key,
        anchor_offset: value.anchor_offset,
        scroll_y: value.scroll_y,
        last_new_post_check_at: value.last_new_post_check_at,
    };
}

function saveRecord(sessionId, record, windowObject = typeof window === 'undefined' ? null : window) {
    const normalized = normalizeRecord(record);
    if (!windowObject || !windowObject.sessionStorage || !validSessionId(sessionId) || !normalized) return false;

    try {
        windowObject.sessionStorage.setItem(storageKey(sessionId), JSON.stringify(normalized));
        return true;
    } catch (_) {
        safeRemove(sessionId, windowObject);
        return false;
    }
}

function loadRecord(sessionId, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage || !validSessionId(sessionId)) return null;
    try {
        const raw = windowObject.sessionStorage.getItem(storageKey(sessionId));
        if (!raw) return null;
        const normalized = normalizeRecord(JSON.parse(raw));
        if (!normalized) safeRemove(sessionId, windowObject);
        return normalized;
    } catch (_) {
        safeRemove(sessionId, windowObject);
        return null;
    }
}

function recordForPage({ filter, viewer, highWaterToken, nextCursor, hasMore, skippedCount, items }) {
    return normalizeRecord({
        v: VERSION,
        filter,
        viewer_marker: viewer,
        high_water_token: highWaterToken || null,
        next_cursor: nextCursor || null,
        has_more: Boolean(hasMore),
        skipped_count: Number(skippedCount || 0),
        descriptors: (items || []).map(item => ({
            key: item.key,
            placement: item.placement === 'pinned' ? 'pinned' : 'organic',
        })),
        anchor_key: null,
        anchor_offset: null,
        scroll_y: 0,
        last_new_post_check_at: 0,
    });
}

function captureAnchor(record, documentObject = typeof document === 'undefined' ? null : document, windowObject = typeof window === 'undefined' ? null : window) {
    if (!record || !documentObject || !windowObject) return record;
    const cards = Array.from(documentObject.querySelectorAll('[data-feed-key]'));
    const anchor = cards.find(card => {
        const rectangle = card.getBoundingClientRect();
        return rectangle.bottom > 0;
    }) || null;

    return normalizeRecord({
        ...record,
        anchor_key: anchor ? anchor.getAttribute('data-feed-key') : null,
        anchor_offset: anchor ? anchor.getBoundingClientRect().top : null,
        scroll_y: Math.max(0, Number(windowObject.scrollY || windowObject.pageYOffset || 0)),
    });
}

function resolveAnchorKey(anchorKey, orderedKeys, survivingKeys) {
    if (!anchorKey || !Array.isArray(orderedKeys)) return null;
    const surviving = survivingKeys instanceof Set ? survivingKeys : new Set(survivingKeys || []);
    if (surviving.has(anchorKey)) return anchorKey;
    const index = orderedKeys.indexOf(anchorKey);
    if (index < 0) return null;

    for (let next = index + 1; next < orderedKeys.length; next += 1) {
        if (surviving.has(orderedKeys[next])) return orderedKeys[next];
    }
    for (let previous = index - 1; previous >= 0; previous -= 1) {
        if (surviving.has(orderedKeys[previous])) return orderedKeys[previous];
    }

    return null;
}

function escapeAttribute(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function restoreScroll(record, documentObject = typeof document === 'undefined' ? null : document, windowObject = typeof window === 'undefined' ? null : window) {
    if (!record || !documentObject || !windowObject) return { corrected: false, anchor: null };
    const ordered = record.descriptors.map(item => item.key);
    const surviving = new Set(Array.from(documentObject.querySelectorAll('[data-feed-key]'))
        .map(card => card.getAttribute('data-feed-key'))
        .filter(Boolean));
    const anchorKey = resolveAnchorKey(record.anchor_key, ordered, surviving);
    if (anchorKey) {
        const card = documentObject.querySelector(`[data-feed-key="${escapeAttribute(anchorKey)}"]`);
        if (card) {
            const targetOffset = record.anchor_key === anchorKey && record.anchor_offset !== null
                ? record.anchor_offset
                : 0;
            windowObject.scrollBy(0, card.getBoundingClientRect().top - targetOffset);
            return { corrected: true, anchor: anchorKey };
        }
    }

    const documentHeight = Math.max(
        documentObject.documentElement ? documentObject.documentElement.scrollHeight : 0,
        documentObject.body ? documentObject.body.scrollHeight : 0,
    );
    const maximum = Math.max(0, documentHeight - Number(windowObject.innerHeight || 0));
    windowObject.scrollTo(0, Math.min(record.scroll_y, maximum));
    return { corrected: true, anchor: null };
}

function isUnmodifiedSameTabClick(event, anchor) {
    if (!event || event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || !anchor) {
        return false;
    }
    const target = anchor.getAttribute('target');
    return !target || target === '_self';
}

function normalizeUrl(value, base) {
    try {
        return new URL(value, base).href;
    } catch (_) {
        return null;
    }
}

function armReturnContext({ sessionId, sourceUrl, destination, viewer, timestamp = now() }, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage || !validSessionId(sessionId)) return false;
    const source = canonicalFeedUrl(sourceUrl, windowObject);
    const target = normalizeUrl(destination, windowObject.location.href);
    const currentViewer = viewer || viewerMarker(windowObject.document);
    const record = loadRecord(sessionId, windowObject);
    if (!source || source !== windowObject.location.href
        || !target || target === source
        || new URL(target).origin !== windowObject.location.origin
        || !validViewerMarker(currentViewer)
        || !record || record.viewer_marker !== currentViewer
        || record.filter !== canonicalFilter(new URL(source).searchParams.get('filter') || 'all')) {
        return false;
    }

    try {
        windowObject.sessionStorage.setItem(PENDING_RETURN_KEY, JSON.stringify({
            v: VERSION,
            session_id: sessionId,
            source_feed_url: source,
            exact_destination: target,
            viewer_marker: currentViewer,
            context_id: randomId(windowObject.crypto),
            created_at: timestamp,
        }));
        windowObject.sessionStorage.setItem(VIEWER_STORAGE_KEY, currentViewer);
        return true;
    } catch (_) {
        return false;
    }
}

function clearPendingReturn(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage) return;
    try {
        windowObject.sessionStorage.removeItem(PENDING_RETURN_KEY);
    } catch (_) {
        // The marker is optional presentation context.
    }
}

function clearHistoryContext(windowObject = typeof window === 'undefined' ? null : window, clearFeed = false) {
    if (!windowObject || !windowObject.history) return false;
    const previous = windowObject.history.state && typeof windowObject.history.state === 'object'
        ? windowObject.history.state
        : {};
    if (!(RETURN_HISTORY_KEY in previous) && (!clearFeed || !(HISTORY_KEY in previous))) return false;

    const next = { ...previous };
    delete next[RETURN_HISTORY_KEY];
    if (clearFeed) delete next[HISTORY_KEY];
    try {
        windowObject.history.replaceState(next, '', windowObject.location.href);
        return true;
    } catch (_) {
        return false;
    }
}

function removeOtherViewerSessions(currentViewer, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage || !validViewerMarker(currentViewer)) return 0;
    const keys = [];
    try {
        for (let index = 0; index < windowObject.sessionStorage.length; index += 1) {
            const key = windowObject.sessionStorage.key(index);
            if (typeof key === 'string' && key.startsWith(STORAGE_PREFIX)) keys.push(key);
        }
    } catch (_) {
        return 0;
    }

    let removed = 0;
    for (const key of keys) {
        try {
            const raw = windowObject.sessionStorage.getItem(key);
            const record = raw ? normalizeRecord(JSON.parse(raw)) : null;
            if (!record || record.viewer_marker !== currentViewer) {
                windowObject.sessionStorage.removeItem(key);
                removed += 1;
            }
        } catch (_) {
            try {
                windowObject.sessionStorage.removeItem(key);
                removed += 1;
            } catch (_) {
                // Storage cleanup is best effort.
            }
        }
    }

    return removed;
}

function synchronizeViewerContext(currentViewer, windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.sessionStorage || !validViewerMarker(currentViewer)) {
        return { changed: false, removed_sessions: 0 };
    }

    let previousViewer = null;
    let pending = null;
    try {
        previousViewer = windowObject.sessionStorage.getItem(VIEWER_STORAGE_KEY);
        const rawPending = windowObject.sessionStorage.getItem(PENDING_RETURN_KEY);
        pending = rawPending ? JSON.parse(rawPending) : null;
    } catch (_) {
        previousViewer = null;
        pending = null;
    }

    const changed = validViewerMarker(previousViewer) && previousViewer !== currentViewer;
    const removedSessions = removeOtherViewerSessions(currentViewer, windowObject);
    const transferred = windowObject.history && windowObject.history.state
        ? windowObject.history.state[RETURN_HISTORY_KEY]
        : null;

    if (changed || (pending && pending.viewer_marker !== currentViewer)) clearPendingReturn(windowObject);
    if (changed || (transferred && transferred.viewer_marker !== currentViewer)) {
        clearHistoryContext(windowObject, changed);
    }

    try {
        windowObject.sessionStorage.setItem(VIEWER_STORAGE_KEY, currentViewer);
    } catch (_) {
        // Viewer transition cleanup remains best effort when storage is unavailable.
    }

    return { changed, removed_sessions: removedSessions };
}

function canonicalFeedUrl(value, windowObject) {
    const normalized = normalizeUrl(value, windowObject.location.href);
    if (!normalized) return null;
    const source = new URL(normalized);
    const keys = Array.from(source.searchParams.keys());
    const filter = canonicalFilter(source.searchParams.get('filter') || 'all');
    if (source.origin !== windowObject.location.origin
        || !/^\/discussions\/?$/.test(source.pathname)
        || source.hash !== ''
        || !filter
        || keys.some(key => key !== 'filter')
        || keys.filter(key => key === 'filter').length > 1) {
        return null;
    }

    return source.href;
}

function validReturnContext(value, windowObject, timestamp = now(), currentViewer = viewerMarker(windowObject ? windowObject.document : null)) {
    const source = value && typeof value.source_feed_url === 'string'
        ? canonicalFeedUrl(value.source_feed_url, windowObject)
        : null;
    return value && typeof value === 'object' && value.v === VERSION
        && validSessionId(value.session_id)
        && validSessionId(value.context_id)
        && value.viewer_marker === currentViewer
        && typeof value.source_feed_url === 'string'
        && typeof value.exact_destination === 'string'
        && Number.isFinite(value.created_at)
        && timestamp >= value.created_at
        && timestamp - value.created_at <= RETURN_TTL_MS
        && normalizeUrl(value.exact_destination, windowObject.location.href) === windowObject.location.href
        && source !== null
        && (() => {
            const record = loadRecord(value.session_id, windowObject);
            return record
                && record.viewer_marker === currentViewer
                && record.filter === canonicalFilter(new URL(source).searchParams.get('filter') || 'all');
        })();
}

function consumePendingReturn(windowObject = typeof window === 'undefined' ? null : window, timestamp = now(), currentViewer = viewerMarker(windowObject ? windowObject.document : null)) {
    if (!windowObject || !windowObject.sessionStorage || !windowObject.history) return null;
    let pending = null;
    try {
        const raw = windowObject.sessionStorage.getItem(PENDING_RETURN_KEY);
        windowObject.sessionStorage.removeItem(PENDING_RETURN_KEY);
        pending = raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
    if (!validReturnContext(pending, windowObject, timestamp, currentViewer)) return null;

    const referrer = normalizeUrl(windowObject.document.referrer, windowObject.location.href);
    if (!referrer || referrer !== pending.source_feed_url) return null;
    const previous = windowObject.history.state && typeof windowObject.history.state === 'object'
        ? windowObject.history.state
        : {};
    windowObject.history.replaceState({ ...previous, [RETURN_HISTORY_KEY]: pending }, '', windowObject.location.href);

    return pending;
}

function detailReturnContext(windowObject = typeof window === 'undefined' ? null : window, timestamp = now(), currentViewer = viewerMarker(windowObject ? windowObject.document : null)) {
    if (!windowObject || !windowObject.history) return null;
    const current = windowObject.history.state && windowObject.history.state[RETURN_HISTORY_KEY];
    if (validReturnContext(current, windowObject, timestamp, currentViewer)) return current;
    if (current) clearHistoryContext(windowObject);

    return consumePendingReturn(windowObject, timestamp, currentViewer);
}

function initializeContextualBack(documentObject = typeof document === 'undefined' ? null : document, windowObject = typeof window === 'undefined' ? null : window) {
    if (!documentObject || !windowObject) return null;
    const currentViewer = viewerMarker(documentObject);
    synchronizeViewerContext(currentViewer, windowObject);
    const link = typeof documentObject.querySelector === 'function'
        ? documentObject.querySelector('[data-feed-context-back]')
        : null;
    if (!link) {
        clearPendingReturn(windowObject);
        clearHistoryContext(windowObject);
        return null;
    }
    const context = detailReturnContext(windowObject, now(), currentViewer);
    link.setAttribute('href', context ? context.source_feed_url : '/discussions');
    if (link.classList) {
        link.classList.toggle('hidden', !context);
        link.classList.toggle('inline-flex', Boolean(context));
    }
    link.hidden = !context;
    if (!context) return null;
    link.addEventListener('click', event => {
        if (!isUnmodifiedSameTabClick(event, link)) return;
        event.preventDefault();
        windowObject.history.back();
    });

    return context;
}

function shouldCheckNewPosts(lastCheckedAt, timestamp = now(), visible = true) {
    return visible && Number.isFinite(lastCheckedAt)
        && timestamp - lastCheckedAt >= NEW_POST_CHECK_INTERVAL_MS;
}

export default {
    VERSION,
    STORAGE_PREFIX,
    HISTORY_KEY,
    RETURN_HISTORY_KEY,
    PENDING_RETURN_KEY,
    VIEWER_STORAGE_KEY,
    RETURN_TTL_MS,
    NEW_POST_CHECK_INTERVAL_MS,
    MAX_DESCRIPTORS,
    MAX_SKIPPED_COUNT,
    randomId,
    storageKey,
    validSessionId,
    canonicalFilter,
    viewerMarker,
    validViewerMarker,
    currentHistorySession,
    writeHistorySession,
    createHistorySession,
    safeRemove,
    normalizeDescriptors,
    normalizeRecord,
    saveRecord,
    loadRecord,
    recordForPage,
    captureAnchor,
    resolveAnchorKey,
    restoreScroll,
    isUnmodifiedSameTabClick,
    armReturnContext,
    clearPendingReturn,
    clearHistoryContext,
    removeOtherViewerSessions,
    synchronizeViewerContext,
    canonicalFeedUrl,
    validReturnContext,
    consumePendingReturn,
    detailReturnContext,
    initializeContextualBack,
    shouldCheckNewPosts,
};
